import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import apiClient from '@/lib/api';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Package, ExternalLink } from 'lucide-react';

const AdminAppsPage = () => {
  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get('/admin/apps?limit=100')
      .then(res => {
        setApps(res.data.apps);
        setTotal(res.data.total);
      })
      .catch(() => toast.error('Failed to load apps'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="w-10 h-10 border-4 border-pista border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-apps-page">
      <p className="text-sm text-muted-foreground">Showing {apps.length} of {total} OAuth applications</p>

      {apps.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold mb-2">No applications</h3>
            <p className="text-muted-foreground">No OAuth apps have been created yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {apps.map((app) => (
            <Card key={app.app_id} data-testid={`admin-app-${app.app_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{app.name}</h3>
                      {app.website_url && (
                        <a href={app.website_url} target="_blank" rel="noopener noreferrer" className="text-pista">
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                    {app.description && (
                      <p className="text-sm text-muted-foreground mt-1">{app.description}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Owner</span>
                    <span className="font-medium">{app.owner?.email || 'Unknown'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Client ID</span>
                    <code className="text-xs font-mono">{app.client_id.substring(0, 20)}...</code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Created</span>
                    <span className="text-xs">{format(new Date(app.created_at), 'MMM d, yyyy')}</span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-1">Scopes</p>
                  <div className="flex flex-wrap gap-1">
                    {app.scopes.map((scope) => (
                      <Badge key={scope} variant="secondary" className="text-xs">{scope}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminAppsPage;
